import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { CalendarX, Download, FileSpreadsheet } from "lucide-react";
import { NeuButton, NeuCard } from "../../components/neu/Neu";
import { exportAbsences, fetchAbsenceReport } from "../../api/admin";

const CHIPS = ["no_check_in", "no_check_out", "late_beyond_allowance", "other"];

export const AbsenceReport = ({ filters }) => {
  const { t, i18n } = useTranslation();
  const [data, setData] = useState(null);

  useEffect(() => {
    fetchAbsenceReport(filters).then(setData).catch(() => setData(null));
  }, [filters]);

  if (!data) return null;

  return (
    <NeuCard data-testid="absence-report">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="label-xs flex items-center gap-2">
          <CalendarX className="h-4 w-4 text-destructive" /> {t("reports.absencesTitle")}
        </p>
        <div className="flex flex-wrap items-center gap-2">
          <NeuButton
            data-testid="absence-export-excel"
            className="flex items-center gap-2 px-3 py-2 text-xs"
            onClick={() => exportAbsences("xlsx", i18n.language, filters)}
          >
            <FileSpreadsheet className="h-4 w-4" /> Excel
          </NeuButton>
          <NeuButton
            data-testid="absence-export-pdf"
            className="flex items-center gap-2 px-3 py-2 text-xs"
            onClick={() => exportAbsences("pdf", i18n.language, filters)}
          >
            <Download className="h-4 w-4" /> PDF
          </NeuButton>
        </div>
      </div>

      <div className="mt-4 flex flex-wrap gap-2" data-testid="absence-totals">
        <span className="rounded-full bg-muted px-3 py-1 text-xs font-bold">
          {t("reports.absenceDays")}: {data.totals.days}
        </span>
        {CHIPS.map((key) => (
          <span key={key} className="rounded-full bg-destructive/10 px-3 py-1 text-xs font-bold text-destructive">
            {t(`reports.reasons.${key}`)}: {data.totals[key]}
          </span>
        ))}
      </div>

      {!data.rows.length ? (
        <p className="mt-4 text-sm text-muted-foreground" data-testid="absence-empty">
          {t("reports.absencesEmpty")}
        </p>
      ) : (
        <div className="mt-4 overflow-x-auto">
          <table className="w-full min-w-[560px] text-start text-sm" data-testid="absence-table">
            <thead>
              <tr className="border-b border-border/60">
                {["reports.employee", "check.branch", "reports.date", "reports.reason"].map((c) => (
                  <th key={c} className="px-3 py-3 text-start text-[11px] tracking-normal text-muted-foreground">
                    {t(c)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data.rows.map((r) => (
                <tr key={r.id} data-testid={`absence-row-${r.id}`} className="border-b border-border/30">
                  <td className="px-3 py-3 font-semibold">{r.employee_name}</td>
                  <td className="px-3 py-3 text-muted-foreground">{r.branch_name}</td>
                  <td className="px-3 py-3 font-mono">{r.date}</td>
                  <td className="px-3 py-3 font-semibold text-destructive">{t(`reports.reasons.${r.reason}`)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </NeuCard>
  );
};
